import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';

import AdminTable from '@/components/admin/AdminTable';
import AdminDataTable from '@/components/admin/AdminDataTable';
import { usersAtom } from '@/interfaces/admin';

import type { ChangeEvent, KeyboardEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import type { TableData, UserData } from '@/@types/admin';

const initialTableData: TableData<UserData> = {
  showCheckbox: true,
  keys: ['status', 'email', 'name', 'tel', 'birthday', 'registerDate', 'editDate'],
  linkKey: 'email',
  linkUrl: '/admin/users/view/detail',
  linkTitle: '회원 상세 조회',
  header: ['번호', '상태', '아이디(이메일)', '이름', '전화번호', '생일', '등록일자', '수정일자'],
  rows: [],
};

export default function UsersView() {
  const { isPending, data } = useAtomValue(usersAtom);

  const [tableData, setTableData] = useState(initialTableData);
  const [userStatus, setUserStatus] = useState('');
  const [searchCriteria, setSearchCriteria] = useState<'all' | keyof UserData>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isPending) return;
    setTableData((prev) => ({ ...prev, rows: data }));
  }, [data, isPending]);

  function onRadioChange(e: ChangeEvent<HTMLInputElement>) {
    setUserStatus(e.target.value);
  }
  function onSelectChange(e: SelectChangeEvent) {
    setSearchCriteria(e.target.value as 'all' | keyof UserData);
  }
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);
  }
  function onQueryKeyUp(e: KeyboardEvent) {
    if (e.code === 'Enter') onSearchClick();
  }
  function onSearchClick() {
    if (isPending) return;

    if (userStatus === '' && query === '') {
      setTableData({ ...tableData, rows: data });
      return;
    }

    tableData.rows = data;
    if (userStatus !== '') {
      tableData.rows = tableData.rows.filter((d) => d.status === userStatus);
    }
    if (query !== '') {
      if (searchCriteria === 'all') {
        tableData.rows = tableData.rows.filter((d) =>
          Object.values(d).some((v) => !['활동', '정지'].includes(v) && v.includes(query)),
        );
      } else {
        tableData.rows = tableData.rows.filter((d) => d[searchCriteria].includes(query));
      }
    }
    setTableData({ ...tableData });
  }
  function onDownloadAllClick() {
    // TODO: Implement download
  }
  function onDownloadSelectedClick() {
    // TODO: Implement download
  }

  return (
    <Stack gap={3}>
      <AdminTable>
        <TableRow>
          <TableCell className="label">회원 상태</TableCell>
          <TableCell>
            <RadioGroup row value={userStatus} onChange={onRadioChange} sx={{ gap: 3 }}>
              <FormControlLabel value="" control={<Radio />} label="전체" />
              <FormControlLabel value="활동" control={<Radio />} label="활동" />
              <FormControlLabel value="정지" control={<Radio />} label="정지" />
            </RadioGroup>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="label">검색분류</TableCell>
          <TableCell>
            <Stack direction="row" gap={2}>
              <Select value={searchCriteria} onChange={onSelectChange} sx={{ width: '15%', bgcolor: 'white' }}>
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="email">아이디(이메일)</MenuItem>
                <MenuItem value="name">이름</MenuItem>
                <MenuItem value="tel">전화번호</MenuItem>
                <MenuItem value="birthday">생일</MenuItem>
              </Select>
              <TextField
                value={query}
                placeholder="검색어를 입력해 주세요."
                onChange={onQueryChange}
                onKeyUp={onQueryKeyUp}
                sx={{ flexGrow: 1, bgcolor: 'white' }}
              />
            </Stack>
          </TableCell>
        </TableRow>
      </AdminTable>
      <Button onClick={onSearchClick} sx={{ alignSelf: 'center' }}>
        검색
      </Button>

      <AdminDataTable data={tableData}>
        <Button onClick={onDownloadAllClick}>검색 회원 다운로드</Button>
        <Button onClick={onDownloadSelectedClick}>선택 회원 다운로드</Button>
      </AdminDataTable>
    </Stack>
  );
}
